// Money, from the kit — the ONE place this app turns an amount into text.
//
// This app had no money in it at all until طرح درمان began quoting the clinic's
// own tariffs. Every Parsina service carries an integer number of MINOR UNITS of
// the document's currency (docs/clinic-currency/CONTRACTS.md): whole RIALS for an
// Iranian clinic, which quotes, types and reads TOMAN — ten rials to the toman —
// and fils, cents or baisa for a clinic that keeps its books in AED, USD or OMR.
// Writing either conversion a second time here is exactly how a 500,000 ت
// treatment ends up printed as 5,000,000 ت on a document a patient signs — which
// has shipped before, in both directions, on one screen.
//
// So the conversions are the kit's (`money.js` for toman, `currency.js` for every
// allowed currency) and this file only COMPOSES them: which figure, which unit
// word, which catalogue sentence. Nothing here divides by anything.
//
// ── The rules it keeps ──────────────────────────────────────────────────────
//   • IRR — and a blank currency, which IS IRR — renders in Persian and
//     English exactly as it always has: `fmtToman` (Persian digits) + this
//     app's own «تومان» / "toman" catalogue word. Byte for byte. French and
//     German are the exceptions (PM-0185, PM-0212): the kit keeps `fmtToman`
//     Persian by design and off their pages, so a French or German page gets
//     the kit's locale formatter («4 500 000» with U+202F / «4.500.000», Latin
//     digits) and the `common.units.tomans` word from 2 up («tomans» in
//     French, the invariable «Toman» in German).
//   • Any other currency renders in its own fraction digits, in the page's
//     digits, beside its ISO code («۱۲٫۵۰ AED»), and never beside «تومان».
//   • Two currencies are never added. There is no exchange rate in Parsina;
//     callers group by currency (`sumByCurrency`) or refuse.

import { fmtToman } from '@parsina/framework/ui/lib/money';
import {
  DEFAULT_CURRENCY, normalizeCurrency, formatAmount, unitLabel, parseAmount,
} from '@parsina/framework/ui/lib/currency';

export * from '@parsina/framework/ui/lib/money';
export {
  DEFAULT_CURRENCY, CURRENCIES, CURRENCY_CODES, isClinicCurrency, normalizeCurrency,
  currencyInfo, toDisplay, fromDisplay, parseAmount, formatAmount, unitLabel,
  assertSameCurrency, sumByCurrency, MixedCurrencyError,
} from '@parsina/framework/ui/lib/currency';

const isIrr = (currency) => normalizeCurrency(currency) === DEFAULT_CURRENCY;

const languageOf = (locale) => String(locale || '').toLowerCase().split(/[-_]/)[0];

// Panel languages whose toman figures use the kit's locale formatter instead of
// `fmtToman`'s Persian digits. Persian and English are deliberately not here:
// their toman figures stay byte for byte what they always were.
const LOCALE_TOMAN = new Set(['fr', 'de']);

// Panel languages whose DECIMAL mark is a comma — where «500,000» typed as a
// toman tariff is ambiguous (a grouped half million, or 500 point zero). German
// groups with '.', so «500.000» is just as ambiguous there.
const COMMA_DECIMAL = new Set(['fr', 'de']);

// Ten rials to the toman: «tomans» begins at 2 toman = 20 rials.
const TWO_TOMAN_IN_RIAL = 20;

/**
 * The unit beside a clinic-money FIGURE: «تومان» / "toman" for IRR (this app's
 * catalogue word, so it reads exactly as before), the ISO code for anything else
 * («AED» in every language — the same chip the kit's MoneyInput shows).
 */
export function moneyUnit(currency, { t, locale, amountMinor } = {}) {
  if (isIrr(currency)) {
    // After an amount of 2 toman or more, the plural form («tomans» in French;
    // «toman» / «تومان» / «Toman» unchanged in English, Persian and German).
    return typeof amountMinor === 'number' && Math.abs(amountMinor) >= TWO_TOMAN_IN_RIAL
      ? t('common.units.tomans')
      : t('common.units.toman');
  }
  return unitLabel(currency, locale, 'short');
}

/**
 * The currency's NAME, for a sentence or a label — «تعرفهٔ هر جلسه (درهم امارات)».
 * IRR is the catalogue's «تومان» / "toman", as those sentences always said.
 */
export function currencyName(currency, { t, locale } = {}) {
  if (isIrr(currency)) return t('common.units.toman');
  return unitLabel(currency, locale, 'long');
}

/**
 * Minor units of `currency` → the bare figure, no unit.
 *
 * IRR is `fmtToman` — Persian digits even in the English UI, because that is what
 * every toman figure in this app has always been, and money is the one thing it
 * must not format a second way. Any other currency follows the page's digits.
 */
export function formatMoneyFigure(amountMinor, currency, { locale } = {}) {
  if (isIrr(currency) && !LOCALE_TOMAN.has(languageOf(locale))) return fmtToman(amountMinor);
  return formatAmount(amountMinor, currency, { locale });
}

/**
 * Minor units of `currency` → «۴٬۵۰۰٬۰۰۰ تومان» / «۱۲٫۵۰ AED».
 *
 * `t` is required: the IRR unit word and the figure-plus-unit order both come from
 * the catalogue (`visit.plan.toman` = «{{amount}} {{unit}}»), which is what keeps
 * every IRR string on this screen identical to what it was before currencies.
 * A code outside the allow-list throws rather than rendering as toman.
 */
export function formatMoney(amountMinor, currency, { t, locale } = {}) {
  return t('visit.plan.toman', {
    amount: formatMoneyFigure(amountMinor, currency, { locale }),
    unit: moneyUnit(currency, { t, locale, amountMinor }),
  });
}

/**
 * A tariff the dentist TYPED → minor units of `currency`, or NaN.
 *
 * `parseAmount` in the panel language, with one refusal on top. Under a
 * comma-decimal language (French, German) the kit reads «500,000» toman as 500
 * point zero — 500 toman for a half million typed — and rounds it silently, because
 * IRR parsing is lenient. A toman tariff has no fraction a dentist types, so
 * there every mark (`,` or `.`) is taken as GROUPING, and only when it is
 * unambiguous: one kind of mark, each group after it exactly three digits.
 * «500,000», «4.500.000» and «4 500 000» read as typed; «12,5», «12,50» and
 * «1.000,25» are refused rather than guessed. Persian and English, and every
 * other currency (strict in the kit already), parse exactly as `parseAmount`.
 */
export function parseTariff(typed, currency, locale) {
  if (!isIrr(currency) || !COMMA_DECIMAL.has(languageOf(locale))) {
    return parseAmount(typed, currency, locale);
  }
  const text = String(typed ?? '').trim();
  const marks = text.match(/[.,]/g);
  if (marks) {
    if (new Set(marks).size > 1) return NaN;
    const groups = text.split(/[.,]/);
    if (groups.slice(1).some((g) => !/^[0-9۰-۹٠-٩]{3}$/.test(g))) return NaN;
  }
  // The marks are grouping by now; the kit reads what is left (spaces, «٬»,
  // Persian digits) exactly as it always has.
  return parseAmount(text.replace(/[.,]/g, ''), currency);
}
